import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'motion/react'
import { useTenantConfig, useCart, useCartActions } from '@webmono/storefront-ui/providers'
import { formatCents } from '@webmono/storefront-ui/utils'
import { EmptyState, EmptyCartIcon } from '@/components/EmptyState'

export function CartPage() {
  const tenant = useTenantConfig()
  const navigate = useNavigate()
  const { items, totals, tableNumber, promoCode } = useCart()
  const { updateItemQuantity, removeItem, clearCart } = useCartActions()

  if (items.length === 0) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12">
        <EmptyState
          icon={<EmptyCartIcon />}
          title="Your cart is empty"
          description="Add some delicious items to get started"
          action={{ label: 'Browse Menu', href: '/menu' }}
        />
      </div>
    )
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading text-xl font-semibold text-foreground">Your Order</h1>
        <button
          onClick={() => clearCart()}
          className="text-sm text-destructive hover:text-destructive/80 transition-colors"
        >
          Clear All
        </button>
      </div>

      {tableNumber && (
        <div className="mb-4 p-3 rounded-lg bg-primary/10 border border-primary/20 flex items-center gap-2">
          <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span className="text-sm font-medium text-primary">Table {tableNumber}</span>
        </div>
      )}

      <div className="space-y-3 mb-6">
        <AnimatePresence mode="popLayout">
          {items.map((item) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95, height: 0 }}
              transition={{ duration: 0.2 }}
              className="flex gap-3 p-3 bg-card rounded-lg">
            {item.menuItem.image && (
              <img
                src={item.menuItem.image}
                alt={item.menuItem.name}
                className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-medium text-foreground leading-tight">{item.menuItem.name}</h3>
                <button
                  onClick={() => removeItem(item.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors p-1"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {item.selectedModifiers.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {item.selectedModifiers.map((m) => m.modifier.name).join(', ')}
                </p>
              )}

              {item.specialInstructions && (
                <p className="text-xs text-muted-foreground mt-1 italic">
                  "{item.specialInstructions}"
                </p>
              )}

              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateItemQuantity(item.id, item.quantity - 1)}
                    className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-foreground hover:bg-muted/80 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="font-medium text-foreground w-6 text-center text-sm">{item.quantity}</span>
                  <button
                    onClick={() => updateItemQuantity(item.id, item.quantity + 1)}
                    className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-foreground hover:bg-muted/80 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
                <span className="font-mono text-sm font-medium text-foreground">
                  {formatCents(item.lineTotalCents, tenant.operations.currency)}
                </span>
              </div>
            </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="border-t border-border pt-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="text-foreground">{formatCents(totals.subtotalCents, tenant.operations.currency)}</span>
        </div>

        {promoCode && totals.discountCents > 0 && (
          <div className="flex justify-between text-sm text-success">
            <span>Discount ({promoCode.code})</span>
            <span>-{formatCents(totals.discountCents, tenant.operations.currency)}</span>
          </div>
        )}

        {totals.loyaltyDiscountCents > 0 && (
          <div className="flex justify-between text-sm text-success">
            <span>Loyalty Reward</span>
            <span>-{formatCents(totals.loyaltyDiscountCents, tenant.operations.currency)}</span>
          </div>
        )}

        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Tax</span>
          <span className="text-foreground">{formatCents(totals.taxCents, tenant.operations.currency)}</span>
        </div>

        {totals.deliveryFeeCents > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Delivery Fee</span>
            <span className="text-foreground">{formatCents(totals.deliveryFeeCents, tenant.operations.currency)}</span>
          </div>
        )}

        <div className="flex justify-between text-lg font-semibold pt-2 border-t border-border">
          <span className="text-foreground">Total</span>
          <span className="text-foreground">{formatCents(totals.totalCents, tenant.operations.currency)}</span>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <button
          onClick={() => navigate('/checkout')}
          className="w-full py-3 rounded-full bg-primary text-primary-foreground font-medium text-center hover:opacity-90 active:scale-[0.98] transition-all"
        >
          Proceed to Checkout
        </button>
        <Link
          to="/menu"
          className="block w-full py-3 rounded-full bg-secondary text-secondary-foreground font-medium text-center hover:bg-secondary/80 transition-colors"
        >
          Add More Items
        </Link>
      </div>
    </div>
  )
}
